import { z } from "zod";
import {
  NotificationCountSchema,
  NotificationPageSchema,
  NotificationSettingsSchema,
  PushConfigSchema,
  type NotificationCount,
  type NotificationPage,
  type NotificationSettings,
  type PushConfig,
  type PushSubscriptionInput,
  type UpdateNotificationSettings,
} from "@lumika/shared";
import { apiFetch } from "./client";

/**
 * Operator notifications API client (Plan 0007). Backend shipped in
 * PR #7 — this wires the in-app feed + settings to it.
 */
export const notificationsApi = {
  list: (
    status: "unread" | "all" = "all",
    before?: string,
  ): Promise<NotificationPage> =>
    apiFetch(
      `/notifications?status=${status}&limit=20${before ? `&before=${encodeURIComponent(before)}` : ""}`,
      NotificationPageSchema,
    ),

  count: (): Promise<NotificationCount> =>
    apiFetch("/notifications/count", NotificationCountSchema),

  markRead: (id: string): Promise<void> =>
    apiFetch(`/notifications/${id}/read`, z.undefined(), {
      method: "PATCH",
    }) as Promise<void>,

  markAllRead: (): Promise<void> =>
    apiFetch("/notifications/read-all", z.undefined(), {
      method: "POST",
    }) as Promise<void>,

  getSettings: (): Promise<NotificationSettings> =>
    apiFetch("/me/notification-settings", NotificationSettingsSchema),

  updateSettings: (body: UpdateNotificationSettings): Promise<NotificationSettings> =>
    apiFetch("/me/notification-settings", NotificationSettingsSchema, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // ----- Web push (UIUX §8.3) -----------------------------------------

  pushConfig: (): Promise<PushConfig> =>
    apiFetch("/notifications/push/config", PushConfigSchema),

  subscribePush: (body: PushSubscriptionInput): Promise<void> =>
    apiFetch("/notifications/push-subscriptions", z.undefined(), {
      method: "POST",
      body: JSON.stringify(body),
    }) as Promise<void>,

  unsubscribePush: (endpoint: string): Promise<void> =>
    apiFetch("/notifications/push-subscriptions", z.undefined(), {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }) as Promise<void>,
};
